import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { biService } from '../services/api';

/**
 * Editor BI embarcado: abre o Superset COMPLETO (autoria) num iframe dentro do
 * NeoGestor (mantendo a barra lateral). A URL vem de GET /bi/editor-url e aponta
 * para a origem mesmo-host (proxy), de modo que o cookie de sessao do Superset
 * seja enviado no iframe. Restrito ao municipio ativo (RLS). Botao "Tela cheia"
 * expande o iframe sobre a viewport sem navegar (preserva a sessao).
 */
const BiEditorPage: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  const load = useCallback(() => {
    if (!municipioAtivo?.id) {
      setError('Selecione um municipio ativo para editar BI.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setUrl(null);
    biService
      .getEditorUrl(municipioAtivo.id)
      .then(({ url }) => setUrl(url))
      .catch((e: any) =>
        setError(e?.response?.data?.message || 'Falha ao abrir o editor BI no Superset.'),
      )
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id]);

  // Recarrega ao montar e ao trocar de municipio (novo token -> novo papel/RLS).
  useEffect(() => {
    load();
  }, [load]);

  const outer = fullscreen
    ? ({ position: 'fixed', inset: 0, zIndex: 1300, bgcolor: '#fff' } as const)
    : ({ width: '100%', height: 'calc(100vh - 64px)', position: 'relative' } as const);

  return (
    <Box sx={outer}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={fullscreen ? <FullscreenExit /> : <Fullscreen />}
          onClick={() => setFullscreen((v) => !v)}
          sx={{ textTransform: 'none', boxShadow: 'none' }}
        >
          {fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ p: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && url && (
        <iframe
          title="Editor BI (Superset)"
          src={url}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      )}
    </Box>
  );
};

export default BiEditorPage;
